import React, { createContext, useContext, useEffect, useState } from "react";
import Icon, { IconResponseData } from "../../core/models/Icon";
import NetworkingServiceError from "../../core/services/network/NetworkServiceError";
import appConfig from "../../util/appConfig";

// Define the error type for Icon operations
export type IconError = NetworkingServiceError;

// Define the shape of the context data
export type IconsDataType = {
  iconsResponseData?: IconResponseData;
  setIconsResponseData: (responseData?: IconResponseData) => void;
  selectedIcon?: Icon;
  setSelectedIcon: (icon?: Icon) => void;
  iconsLoading: boolean;
  setIconsLoading: (isLoading: boolean) => void;
  iconsError: IconError | undefined;
  setIconsError: (error?: IconError) => void;
};

// Create the context with an initial undefined value
export const IconsDataContext = createContext<IconsDataType | undefined>(
  undefined
);

// Provider component to manage and provide the context value
export const IconsDataProvider = ({ children }: any) => {
  const [selectedIcon, setSelectedIcon] = useState<Icon>();
  const [iconsResponseData, setIconsResponseData] =
    useState<IconResponseData>();
  const [iconsLoading, setIconsLoading] = useState<boolean>(false);
  const [iconsError, setIconsError] = useState<IconError>();

  // Log selectedIcon changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugIcons && selectedIcon) {
      console.debug("[Icons][SelectedIcon]", selectedIcon);
    }
  }, [selectedIcon]);

  // Log iconsResponseData changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugIcons && iconsResponseData) {
      console.debug("[Icons][IconsResponseData]", iconsResponseData);
    }
  }, [iconsResponseData]);

  // Log IconsLoading changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugIcons && iconsLoading) {
      console.debug("[Icons][IconsLoading]", iconsLoading);
    }
  }, [iconsLoading]);

  // Log iconsError changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugIcons && iconsError) {
      console.warn(
        "[Icons][Error]",
        iconsError?.userMessage(),
        iconsError?.debugMessage()
      );
    }
  }, [iconsError]);

  // Context value to be provided to consumers
  const value = {
    iconsResponseData,
    setIconsResponseData,
    selectedIcon,
    setSelectedIcon,
    iconsLoading,
    setIconsLoading,
    iconsError,
    setIconsError,
  };

  return (
    <IconsDataContext.Provider value={value}>
      {children}
    </IconsDataContext.Provider>
  );
};

// Custom hook to use the IconsDataContext
export const useIconsContext = () => {
  const context = useContext(IconsDataContext);
  if (context === undefined) {
    throw new Error("useIconsContext must be used within a IconsDataProvider");
  }
  return context;
};
